import { useCallback, useEffect, useMemo, useState } from 'react';
import { Hash, Megaphone, MessageSquare, MessagesSquare, UsersRound } from 'lucide-react';
import type { Me } from '../api/auth';
import { familyConversationsApi } from '../api/conversations';
import type { EventThreadPage, FamilyConversationRow, UnreadSummary } from '../api/conversations';
import { monthGroupsOf, toListRow as toEventRow } from '../lib/eventMonths';
import { useLiveMessages } from '../hooks/useLiveMessages';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { EmptyState } from '../ui/EmptyState';
import { EventThreadSection, ThreadSection } from '../ui/ThreadList';
import type { EventSort, ThreadListRow } from '../ui/ThreadList';

/**
 * A parent's conversations, across every unit their children are in.
 *
 * **The same page as a member's, through the door a guardian actually has.** A guardian holds no
 * membership, so `UnitScopeMiddleware` 404s them on every `/units/` route before an endpoint runs —
 * which is not a quirk to work around but the whole reason this route exists (rule 13b). It mirrors
 * `/units` in routes and must never mirror it in rules: the sections, the bounds and the rows are
 * the same components over the same policy, and everything that differs here is a difference in the
 * data the two endpoints return.
 */
type SectionKey = 'channels' | 'groups' | 'threads' | 'events' | 'announcements' | 'direct';

function sectionOf(row: FamilyConversationRow): SectionKey {
  if (row.anchor === 'Event') return 'events';
  if (row.anchor === 'Announcement') return 'announcements';
  if (row.audienceMode === 'Explicit') return 'direct';
  if (!row.isStandingChannel) return 'threads';
  return row.audienceSubgroupId ? 'groups' : 'channels';
}

const PAGES_ON_OPEN = 3;

/**
 * The children named on a row, bounded.
 *
 * A whole-unit thread contains every child a parent has, and the dev database has given Reese
 * Lindqvist fifty-odd of them all called Rowan — separate people with separate ids, so nothing
 * dedupes them (TROOP-77). Joining the list produced a caption 1,200 characters long on the first
 * row of the page. Two names and a count says the same thing and fits on the line.
 */
function childrenIn(names: string[]): string {
  if (names.length <= 2) {
    return names.join(' and ');
  }

  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
}

/**
 * The caption under a parent's row: which unit, and why they can read it.
 *
 * **Why matters more here than anywhere else in the product.** A parent reading a thread they are
 * not in is exactly the access this page exists to give them, and it must never look like
 * eavesdropping — so the row says which of their children is in it. `Guardian` is I4: entitled to
 * look in, never to join in. `Participant` and `Monitor` mean they are in it in their own right,
 * which is what the parents-and-leaders channel is — and that row names no child at all (I5),
 * which is perfectly ordinary rather than a gap.
 */
function metaFor(row: FamilyConversationRow): string {
  const parts = [row.unitName];

  if (row.childNames.length > 0) {
    // "is" for one child and "are" for several. A caption a parent reads on every row of this
    // page is not the place to be ungrammatical about their own children.
    const isAre = row.childNames.length === 1 ? 'is' : 'are';

    parts.push(
      row.basis === 'Guardian'
        ? `You can read this because ${childrenIn(row.childNames)} ${isAre} in it`
        : `With ${childrenIn(row.childNames)}`,
    );
  } else if (row.basis === 'Guardian') {
    parts.push('You can read this as a parent');
  }

  return parts.join(' · ');
}

function toListRow(row: FamilyConversationRow): ThreadListRow {
  return {
    id: row.id,
    title: row.title,
    preview: row.preview,
    lastMessageAt: row.lastMessageAt,
    unreadCount: row.unreadCount,
    meta: metaFor(row),
  };
}

export function FamilyConversationsScreen({
  me,
  onNavigate,
}: {
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const live = useLiveMessages();

  const [channels, setChannels] = useState<FamilyConversationRow[] | null>(null);
  const [events, setEvents] = useState<EventThreadPage | null>(null);
  const [eventSort, setEventSort] = useState<EventSort>('date');
  const [movingMonth, setMovingMonth] = useState(false);
  const [feed, setFeed] = useState<FamilyConversationRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [unread, setUnread] = useState<UnreadSummary | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [channelPage, eventPage, summary] = await Promise.all([
        familyConversationsApi.channels(),
        familyConversationsApi.events(),
        familyConversationsApi.unreadTotal(),
      ]);

      const rows: FamilyConversationRow[] = [];
      let next: string | null = null;

      for (let page = 0; page < PAGES_ON_OPEN; page++) {
        const result = await familyConversationsApi.list(next ?? undefined);
        rows.push(...result.rows);
        next = result.nextCursor;
        if (!next) break;
      }

      setChannels(channelPage.rows);
      setEvents(eventPage);
      setFeed(rows);
      setCursor(next);
      setUnread(summary);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load conversations.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The same stream, and deliberately no unit filter: a parent's list spans every unit their
  // children are in, and the server has already decided this nudge was theirs to hear.
  useEffect(() => {
    if (live.tick === 0) return;
    void load();
  }, [live.tick, load]);

  async function loadMore() {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await familyConversationsApi.list(cursor);
      setFeed((current) => [...current, ...result.rows]);
      setCursor(result.nextCursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load more.');
    } finally {
      setLoadingMore(false);
    }
  }

  const sections = useMemo(() => {
    const all = channels ?? [];

    return {
      channels: all.filter((r) => !r.audienceSubgroupId).map(toListRow),
      groups: all.filter((r) => r.audienceSubgroupId).map(toListRow),
      threads: feed.filter((r) => sectionOf(r) === 'threads').map(toListRow),
      announcements: feed.filter((r) => sectionOf(r) === 'announcements').map(toListRow),
      direct: feed.filter((r) => sectionOf(r) === 'direct').map(toListRow),
    };
  }, [channels, feed]);

  const eventMonths = useMemo(() => monthGroupsOf(events?.rows ?? []), [events]);
  const recentEvents = useMemo(() => (events?.recent ?? []).map(toEventRow), [events]);

  async function moveTo(month: string) {
    setMovingMonth(true);
    try {
      setEvents(await familyConversationsApi.events(month));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load that month.');
    } finally {
      setMovingMonth(false);
    }
  }

  const open = (conversationId: string) => onNavigate(`/family/conversations/${conversationId}`);

  const nothingAtAll =
    channels !== null &&
    Object.values(sections).every((rows) => rows.length === 0) &&
    (events?.rows.length ?? 0) === 0;

  return (
    <Page>
      <AppHeader me={me} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              Your family
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 var(--space-4)' }}
            >
              Conversations
            </h1>

            <Button variant="secondary" onClick={() => onNavigate('/family')}>
              Back to your family
            </Button>
          </div>
        </Gutter>
      </section>

      <Gutter>
        <div style={{ paddingBlock: 'var(--space-6)' }}>
          {error && (
            <p className="t-body" style={{ color: 'var(--color-status-blocked)' }}>
              {error}
            </p>
          )}

          {channels === null && !error && (
            <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
              Loading&hellip;
            </p>
          )}

          {nothingAtAll && (
            <EmptyState
              icon={MessageSquare}
              headline="Nothing has been said yet"
              explanation="Threads from your children's units appear here — their unit's channels, and the thread on every event and announcement."
            />
          )}

          {channels !== null && !nothingAtAll && (
            <div className="flex flex-col" style={{ gap: 'var(--space-6)' }}>
              {/* The unit's own word for its groups is on each row rather than in this heading:
                  a parent with a child in a Troop and one in a Pack would otherwise need the
                  heading to say "Patrols and Dens", which is nobody's word for anything. */}
              <ThreadSection
                icon={Hash}
                title="Channels"
                rows={sections.channels}
                unread={unread?.byGroup.channels ?? 0}
                onOpen={open}
              />

              <ThreadSection
                icon={UsersRound}
                title="Groups"
                rows={sections.groups}
                unread={unread?.byGroup.groups ?? 0}
                onOpen={open}
              />

              <ThreadSection
                icon={MessagesSquare}
                title="Other threads"
                rows={sections.threads}
                unread={unread?.byGroup.otherThreads ?? 0}
                partial={cursor !== null}
                onOpen={open}
              />

              <EventThreadSection
                months={eventMonths}
                recent={recentEvents}
                index={events?.months ?? []}
                unread={unread?.byGroup.events ?? 0}
                sort={eventSort}
                busy={movingMonth}
                onSortChange={setEventSort}
                onAnchorChange={(month) => void moveTo(month)}
                onOpen={open}
              />

              <ThreadSection
                icon={Megaphone}
                title="Announcements"
                rows={sections.announcements}
                unread={unread?.byGroup.announcements ?? 0}
                partial={cursor !== null}
                onOpen={open}
              />

              <ThreadSection
                icon={MessageSquare}
                title="Direct"
                rows={sections.direct}
                unread={unread?.byGroup.direct ?? 0}
                partial={cursor !== null}
                onOpen={open}
              />

              {cursor !== null && (
                <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
                  <Button variant="secondary" onClick={() => void loadMore()}>
                    {loadingMore ? 'Loading…' : 'Load more threads'}
                  </Button>
                  <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
                    Announcements and other threads fill in as you load. Your children&rsquo;s channels
                    and events are all here already.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </Gutter>

      <Footer />
    </Page>
  );
}
